import { useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';
import { escapeToTrigger } from '@/features/common/escapeToTrigger';
import { nextIndex, typeAheadIndex } from '@/features/common/listboxKeys';

// How long typed letters add up to one prefix before the next one starts a new search.
const TYPE_AHEAD_MS = 500;

/**
 * The keyboard of Dropdown's listbox (WAI-ARIA listbox pattern), where the list is opened and
 * closed by its caller: the focus goes into the list when it opens, onto the selected option (the
 * first, or the last when the up arrow opened it), and moves with the arrows, Home, End and typed
 * letters; Escape closes it and gives the focus back to the trigger, Tab closes it and lets the
 * focus go on. Enter and Space choose, as the options are buttons.
 *
 * The focus goes back to the trigger here and not in useDismiss: a click outside also dismisses,
 * and there the focus belongs where the pointer put it.
 */
export function useListboxKeys(
  isOpen: boolean,
  labels: string[],
  selectedIndex: number,
  triggerRef: RefObject<HTMLButtonElement>,
  onToggle: () => void,
  onClose: () => void,
) {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const openAt = useRef<'first' | 'last' | null>(null);
  const typed = useRef({ text: '', at: 0 });

  const focusAt = (index: number) => optionRefs.current[index]?.focus({ preventScroll: true });
  // Read from the page rather than kept: a click on an option moves the focus too.
  const focusedIndex = () =>
    optionRefs.current.findIndex((option) => option !== null && option === document.activeElement);

  useEffect(() => {
    if (!isOpen) return;
    const last = labels.length - 1;
    const target =
      openAt.current === 'first'
        ? 0
        : openAt.current === 'last'
          ? last
          : selectedIndex >= 0
            ? selectedIndex
            : 0;
    openAt.current = null;
    if (labels.length > 0) focusAt(target);
    // Only on opening: a multi-select's list stays open while its options are picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    if (isOpen) {
      focusAt(e.key === 'ArrowDown' ? 0 : labels.length - 1);
      return;
    }
    openAt.current = e.key === 'ArrowDown' ? 'first' : 'last';
    onToggle();
  };

  const escape = escapeToTrigger(onClose, triggerRef);

  const onListKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (escape(e)) return;
    if (e.key === 'Tab') {
      // The focus steps off the trigger, the option it is on being about to unmount.
      triggerRef.current?.focus();
      onClose();
      return;
    }
    const next = nextIndex(Math.max(focusedIndex(), 0), e.key, labels.length);
    if (next !== null) {
      e.preventDefault();
      focusAt(next);
      return;
    }
    if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = Date.now();
      const text = now - typed.current.at > TYPE_AHEAD_MS ? e.key : typed.current.text + e.key;
      typed.current = { text, at: now };
      const match = typeAheadIndex(labels, text, focusedIndex());
      if (match !== null) {
        e.preventDefault();
        focusAt(match);
      }
    }
  };

  return { optionRefs, onTriggerKeyDown, onListKeyDown };
}
